import { useRef, useState } from "preact/compat"
import { parseISO, differenceInDays } from "date-fns"
import { api_fetch } from "@/api/client"
import { KeheUdrDeduction } from "@/api/integration"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { currencyFormatter } from "@/utils"

interface UdrDeductionsTableProps {
  deductions: KeheUdrDeduction[]
  onUpdated: (deduction: KeheUdrDeduction) => void
}

export function calculate_udr_display_amount(row: KeheUdrDeduction): number | null {
  if (row.total != null) {
    return row.total
  }
  if (row.quantity != null && row.unit_amount != null) {
    return row.quantity * row.unit_amount
  }
  return null
}

interface EditableNumberCellProps {
  value: number | null
  placeholder: string
  onSave: (value: number | null) => void
}

function EditableNumberCell({ value, placeholder, onSave }: EditableNumberCellProps) {
  const [editing, setEditing] = useState(false)
  const [localValue, setLocalValue] = useState(value?.toString() ?? "")
  const lastPropValue = useRef(value)

  if (value !== lastPropValue.current) {
    lastPropValue.current = value
    setLocalValue(value?.toString() ?? "")
  }

  function handleBlur() {
    setEditing(false)
    const trimmed = localValue.trim()
    const parsed = trimmed === "" ? null : Number(trimmed)
    if (trimmed !== "" && Number.isNaN(parsed)) {
      setLocalValue(value?.toString() ?? "")
      return
    }
    if (parsed !== value) {
      onSave(parsed)
    }
  }

  return editing ? (
    <Input
      type="number"
      step="0.01"
      class="h-8 w-24"
      value={localValue}
      onInput={(e: Event) => setLocalValue((e.currentTarget as HTMLInputElement).value)}
      onBlur={handleBlur}
      autoFocus
    />
  ) : (
    <button
      type="button"
      class="text-left text-sm hover:underline"
      onClick={() => setEditing(true)}>
      {value != null ? value : <span class="text-gray-400">{placeholder}</span>}
    </button>
  )
}

export function UdrDeductionsTable({ deductions, onUpdated }: UdrDeductionsTableProps) {
  if (deductions.length === 0) {
    return <p class="text-sm text-gray-500">No pending UDR deductions yet.</p>
  }

  async function saveField(deductionId: string, field: "quantity" | "unit_amount", value: number | null) {
    const res = await api_fetch<KeheUdrDeduction>(`/integration/kehe/udr/deductions/${deductionId}`, {
      method: "PATCH",
      body: { [field]: value },
    })
    if (res.ok) {
      onUpdated(res.value.data)
    }
  }

  return (
    <div class="overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead>
          <tr class="border-b text-left text-gray-500">
            <th class="py-2 pr-4 font-medium">PO #</th>
            <th class="py-2 pr-4 font-medium">Received</th>
            <th class="py-2 pr-4 font-medium">Quantity</th>
            <th class="py-2 pr-4 font-medium">Case cost</th>
            <th class="py-2 pr-4 font-medium">Amount</th>
            <th class="py-2 pr-4 font-medium">UDR PDF</th>
            <th class="py-2 font-medium">Deduction</th>
          </tr>
        </thead>
        <tbody>
          {deductions.map((row: KeheUdrDeduction) => {
            const displayAmount = calculate_udr_display_amount(row)
            return (
              <tr key={row.id} class="border-b border-gray-100">
                <td class="py-2 pr-4">{row.po_number ?? "—"}</td>
                <td class="py-2 pr-4">{new Date(row.created_at).toLocaleDateString()}</td>
                <td class="py-2 pr-4">
                  <EditableNumberCell
                    value={row.quantity}
                    placeholder="Enter qty"
                    onSave={(value: number | null) => saveField(row.id, "quantity", value)}
                  />
                </td>
                <td class="py-2 pr-4">
                  <EditableNumberCell
                    value={row.unit_amount}
                    placeholder="Enter cost"
                    onSave={(value: number | null) => saveField(row.id, "unit_amount", value)}
                  />
                </td>
                <td class="py-2 pr-4">
                  {displayAmount != null ? currencyFormatter(displayAmount) : currencyFormatter(row.invoice_amount)}
                </td>
                <td class="py-2 pr-4">
                  {row.udr_pdf_s3_uri ? (
                    <a
                      class="text-blue-600 hover:underline"
                      href={`/api/_s3?s3_uri=${encodeURIComponent(row.udr_pdf_s3_uri)}`}
                      target="_blank"
                      rel="noopener noreferrer">
                      View PDF
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                <td class="py-2">
                  <a class="text-blue-600 hover:underline" href={`/deduction/${row.id}`}>
                    Open
                  </a>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("calculate_udr_display_amount", () => {
    it("prefers backup total when present", () => {
      const amount = calculate_udr_display_amount({
        id: "1",
        po_number: "123",
        invoice_number: "PENDING UDR PO#123",
        invoice_amount: 100,
        created_at: "2026-01-01T00:00:00Z",
        backup_id: null,
        quantity: 10,
        unit_amount: 12,
        total: 99,
        udr_pdf_s3_uri: null,
      })
      expect(amount).toBe(99)
    })

    it("multiplies quantity and unit amount when total missing", () => {
      const amount = calculate_udr_display_amount({
        id: "1",
        po_number: "123",
        invoice_number: "PENDING UDR PO#123",
        invoice_amount: 100,
        created_at: "2026-01-01T00:00:00Z",
        backup_id: null,
        quantity: 4,
        unit_amount: 5,
        total: null,
        udr_pdf_s3_uri: null,
      })
      expect(amount).toBe(20)
    })
  })
}
